import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../../types';
import { createLanguage } from '../../api';
import { useSettings } from '../../contexts/SettingsContext';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import { ISO_LANGUAGES } from '../../data/languages';

interface LanguageDropdownProps {
  selectedLanguage: Language | null;
  onLanguageSelect: (language: Language | null) => void;
  placeholder?: string;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  selectedLanguage,
  onLanguageSelect,
  placeholder
}) => {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('language.selectLanguage');
  const { settings } = useSettings();
  const { languages } = useReferenceData();
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);

  const getLanguageName = (language: Language) => {
    // Languages should always have ISO codes - if not, something is wrong
    if (!language.code) {
      
      return language.name;
    }
    
    // First try to get i18n translation using the language code
    const translationKey = `languages.${language.code}`;
    const translated = t(translationKey);
    
    // If translation exists, use it
    if (translated !== translationKey) {
      return translated;
    }
    
    // Fallback to English name from ISO list
    const isoLang = ISO_LANGUAGES.find(iso => iso.code === language.code);
    return isoLang?.name || language.name;
  };

  useEffect(() => {
    const loadAvailableLanguages = async () => {
      setLoading(true);
      try {
        // Get user's selected available languages from context
        const userSelectedCodes = settings?.available_languages || [];
        
        // Use languages from shared context
        const allLanguages = languages;
        
        if (userSelectedCodes.length > 0) {
          // Filter to only show user's selected languages
          const filteredLanguages = allLanguages.filter(lang => 
            userSelectedCodes.includes(lang.code || '')
          );
          
          // Always include the currently selected language, even if not in user preferences
          if (selectedLanguage && !filteredLanguages.find(lang => lang.id === selectedLanguage.id)) {
            filteredLanguages.push(selectedLanguage);
          }
          
          // Add any missing languages from ISO list that user selected
          const missingLanguages = userSelectedCodes.filter(code => 
            !filteredLanguages.find(lang => lang.code === code)
          );
          
          for (const code of missingLanguages) {
            const isoLang = ISO_LANGUAGES.find(iso => iso.code === code);
            if (isoLang) {
              try {
                const newLang = await createLanguage({
                  name: isoLang.name,
                  code: isoLang.code
                });
                filteredLanguages.push(newLang);
              } catch (error) {
                
              }
            }
          }
          
          setAvailableLanguages(filteredLanguages);
        } else {
          // If no preferences set, show all existing languages
          setAvailableLanguages(allLanguages);
        }
      } catch (error) {
        
        // Fallback to all languages from context
        setAvailableLanguages(languages);
      } finally {
        setLoading(false);
      }
    };

    if (languages.length > 0) {
      loadAvailableLanguages();
    }
  }, [selectedLanguage, settings, languages]);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const languageId = parseInt(e.target.value);
    if (isNaN(languageId)) {
      onLanguageSelect(null);
    } else {
      const language = availableLanguages.find(lang => lang.id === languageId);
      onLanguageSelect(language || null);
    }
  };

  if (loading) {
    return (
      <select
        disabled
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
      >
        <option>{t('common.loading')}...</option>
      </select>
    );
  }

  return (
    <select
      value={selectedLanguage?.id || ''}
      onChange={handleLanguageChange}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="">{defaultPlaceholder}</option>
      {availableLanguages
        .sort((a, b) => getLanguageName(a).localeCompare(getLanguageName(b)))
        .map(language => (
          <option key={language.id} value={language.id}>
            {getLanguageName(language)}
          </option>
        ))}
    </select>
  );
};

export default LanguageDropdown;